import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { Observable, from, of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { PackageTrackingService } from 'src/pages/admin/logistics/shipping-logs/detail/overlays/package-tracking/package-tracking.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { createPackageTracking, createPackageTrackingFailure, createPackageTrackingSuccess, deletePackageTracking, deletePackageTrackingFailure, deletePackageTrackingSuccess, loadPackageTrackings, loadPackageTrackingsFailure, loadPackageTrackingsSuccess, updatePackageTracking, updatePackageTrackingFailure, updatePackageTrackingSuccess } from './package-tracking.actions'
import { PackageTrackingRecord } from './package-tracking.model'

@Injectable()
export class PackageTrackingEffects {
  private actions$ = inject(Actions)
  private packageTrackings = inject(PackageTrackingService)
  private toaster = inject(ToasterService)


  loadPackageTrackings$ = createEffect(() => this.actions$.pipe(
    ofType(loadPackageTrackings),
    switchMap(action =>
      from(this.packageTrackings.getPackagesByDeal(action.dealId)).pipe(
        map((packageTrackings: PackageTrackingRecord[]) => loadPackageTrackingsSuccess({ dealId: action.dealId, packageTrackings })),
        catchAndLog(this.toaster, 'Unable to fetch package trackings', loadPackageTrackingsFailure)))))

  createPackageTracking$ = createEffect(() => this.actions$.pipe(
    ofType(createPackageTracking),
    tap(() => this.toaster.progress('Creating package tracking...')),
    switchMap(action =>
      from(this.packageTrackings.create(action.packageTracking)).pipe(
        switchMap((stored: PackageTrackingRecord): Observable<Action> =>
          from(this.packageTrackings.update(stored)).pipe(
            tap(() => this.toaster.success('Package tracking created successfully.')),
            map((packageTracking: PackageTrackingRecord) => createPackageTrackingSuccess({ packageTracking })),
            catchError(() => {
              this.toaster.warning('Package tracking created but your tracking number looks invalid.')
              return of(createPackageTrackingSuccess({ packageTracking: stored }))
            }))),
        catchAndLog(this.toaster, 'Unable to create package tracking.', createPackageTrackingFailure)))))

  updatePackageTracking$ = createEffect(() => this.actions$.pipe(
    ofType(updatePackageTracking),
    tap(() => this.toaster.progress('Updating package tracking...')),
    switchMap(action =>
      from(this.packageTrackings.update({ ...action.packageTracking, package_id: action.id })).pipe(
        tap(() => this.toaster.success('Package tracking updated successfully.')),
        map((packageTracking: PackageTrackingRecord) => updatePackageTrackingSuccess({ packageTracking })),
        catchError((err): Observable<Action> => {
          console.error('Unable to update package tracking', err)
          this.toaster.warning('Invalid tracking number.')
          return of(updatePackageTrackingFailure({ error: err }))
        })))))

  deletePackageTracking$ = createEffect(() => this.actions$.pipe(
    ofType(deletePackageTracking),
    tap(() => this.toaster.progress('Deleting package tracking...')),
    switchMap(action =>
      from(this.packageTrackings.remove({ package_id: action.id })).pipe(
        map(() => deletePackageTrackingSuccess({ id: action.id })),
        tap(() => this.toaster.success('Packaging type deleted successfully.')),
        catchAndLog(this.toaster, 'Unable to delete package tracking', deletePackageTrackingFailure)))))
}
